import { useState } from "react";
import { cn } from "@/lib/utils";
import type { RolloutBucket } from "@/lib/runnerReadiness";

/** Per-bucket warm-target list for a runner (Fleet + Fleet Flow pages):
 *  status dot, label, status, and — for a bucket whose last build failed —
 *  its reason class, exit code and the extracted compiler error lines
 *  (internal/runner warmTargetPrewarm.ts `lastFailure`, 2026-10-07). */
export function WarmBucketList({
  buckets,
  layout = "inline",
}: {
  buckets: RolloutBucket[];
  layout?: "inline" | "list";
}) {
  if (buckets.length === 0) return null;
  const failed = buckets.filter((b) => b.lastFailure);
  return (
    <div className="space-y-1">
      <div
        className={cn(
          layout === "inline"
            ? "flex flex-wrap gap-x-3 gap-y-0.5"
            : "space-y-0.5",
        )}
      >
        {buckets.map((b) => (
          <div key={b.label} className="flex items-center gap-1.5">
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                b.status === "warm"
                  ? "bg-green-600"
                  : b.status === "prewarming"
                    ? "bg-blue-500 animate-pulse"
                    : b.status === "failed"
                      ? "bg-destructive"
                      : "bg-muted-foreground",
              )}
            />
            <span className="font-mono">{b.label}</span>
            <span
              className={cn(
                b.status === "failed"
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {b.status}
              {b.lastFailure
                ? ` (${b.status === "failed" ? "" : "last: "}${b.lastFailure.reason}${
                    b.lastFailure.exitCode !== null
                      ? `, exit ${b.lastFailure.exitCode}`
                      : ""
                  })`
                : ""}
            </span>
          </div>
        ))}
      </div>
      {failed.map((b) => (
        <BucketFailureDetail key={b.label} bucket={b} />
      ))}
    </div>
  );
}

function BucketFailureDetail({ bucket }: { bucket: RolloutBucket }) {
  const [open, setOpen] = useState(false);
  const f = bucket.lastFailure;
  if (!f) return null;
  const when = f.ts ? new Date(f.ts).toLocaleString() : "unknown time";
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-primary hover:underline"
      >
        {open ? "▼" : "▶"} {bucket.label} last failure: {f.reason}
        {f.exitCode !== null ? `, exit ${f.exitCode}` : ""} · {when}
        {f.version ? ` · v${f.version}` : ""}
      </button>
      {open && (
        <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-[10px]">
          {f.errorLines.length > 0
            ? f.errorLines.join("\n")
            : "(no error lines extracted)"}
        </pre>
      )}
    </div>
  );
}
